import axios from 'axios'
import { buildApiUrl, API_ENDPOINTS } from '../config/api.js'
import { buildProcessingSummaryPayload } from './processingSummaryExportUtils.js'

/**
 * Excel Download Utilities
 * Provides reusable functions for downloading processed data as Excel files
 */

/**
 * Downloads selected data for a single subject
 * @param {Object} params - Download parameters
 * @param {string} params.subid - Subject ID
 * @param {string} params.datasetIdentifier - Dataset identifier (default: '001')
 * @param {Object} params.downloadOptions - Selected download options
 * @param {Object} params.processedData - Processed data object
 * @returns {Promise} - Download promise
 */
export async function downloadSubjectData({ 
  subid, 
  datasetIdentifier = '001', 
  downloadOptions, 
  processedData 
}) {
  try {
    console.log('Downloading subject data:', {
      subid,
      datasetIdentifier,
      downloadOptions,
      processedDataKeys: Object.keys(processedData || {})
    })

    // Prepare the data for download
    const downloadData = {
      subid,
      dataset_identifier: datasetIdentifier,
      download_options: downloadOptions,
      processed_data: {
        configured_data: processedData?.processed_dataset || processedData?.data || [],
        curve_features: processedData?.curve_features || [],
        day_level_data: processedData?.day_level_data || [],
        ...buildProcessingSummaryPayload(processedData, {
          subid,
          dataset_identifier: datasetIdentifier
        })
      }
    }

    // Make API call to download endpoint
    const response = await axios.post(buildApiUrl(API_ENDPOINTS.DOWNLOAD_DATA), downloadData, {
      responseType: 'blob',
      withCredentials: true
    })

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${subid}_${datasetIdentifier}_processed_data.zip`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    console.log('Subject data download completed successfully')
    return { success: true }
  } catch (error) {
    console.error('Subject data download failed:', error)
    throw error
  }
}

/**
 * Downloads selected data for a cohort
 * @param {Object} params - Download parameters
 * @param {string} params.cohortId - Cohort identifier
 * @param {Object} params.downloadOptions - Selected download options
 * @param {Object} params.cohortData - Cohort data object
 * @param {Object} params.columnMetadata - Column ordering metadata
 * @returns {Promise} - Download promise
 */
export async function downloadCohortData({ 
  cohortId, 
  downloadOptions, 
  cohortData,
  columnMetadata = {}
}) {
  try {
    console.log('Downloading cohort data:', {
      cohortId,
      downloadOptions,
      cohortDataKeys: Object.keys(cohortData || {}),
      hasColumnMetadata: !!columnMetadata
    })

    // Compile curve features and day level data across all subjects
    const compiledData = compileCohortDataForDownload(cohortData, downloadOptions, columnMetadata)

    // Prepare the data for download - use the same structure as single subject
    const downloadData = {
      subid: cohortId, // Use cohort ID as the "subject" ID for naming
      dataset_identifier: 'cohort', // Use 'cohort' as dataset identifier
      download_options: downloadOptions,
      processed_data: compiledData
    }

    // Make API call to download endpoint
    const response = await axios.post(buildApiUrl(API_ENDPOINTS.DOWNLOAD_DATA), downloadData, {
      responseType: 'blob',
      withCredentials: true
    })

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${cohortId}_processed_data.zip`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    console.log('Cohort data download completed successfully')
    return { success: true }
  } catch (error) {
    console.error('Cohort data download failed:', error)
    throw error
  }
}

/**
 * Validates download options to ensure at least one option is selected
 * @param {Object} downloadOptions - Download options object
 * @returns {boolean} - True if at least one option is selected
 */
export function hasSelectedDownloadOptions(downloadOptions) {
  return Object.values(downloadOptions).some(option => option === true)
}

/**
 * Creates default download options for subject data
 * @returns {Object} - Default download options
 */
export function createSubjectDownloadOptions() {
  return {
    processed_dataset: true,
    curve_features: false,
    day_level_data: false,
    processing_summary: true
  }
}

/**
 * Creates default download options for cohort data
 * @returns {Object} - Default download options
 */
export function createCohortDownloadOptions() {
  return {
    curve_features: true,  // Default to true since this is the main value for cohorts
    day_level_data: true,  // Default to true since this is the main value for cohorts
    processing_summary: true
  }
}

/**
 * Calculates total curve features across all subjects in a cohort
 * @param {Array} processedSubjects - Array of processed subject data
 * @returns {number} - Total number of curve features
 */
export function calculateTotalCurveFeatures(processedSubjects) {
  if (!processedSubjects || !Array.isArray(processedSubjects)) return 0
  return processedSubjects.reduce((total, subject) => 
    total + (subject.curve_features?.length || 0), 0
  )
}

/**
 * Calculates total day-level data entries across all subjects in a cohort
 * @param {Array} processedSubjects - Array of processed subject data
 * @returns {number} - Total number of day-level entries
 */
export function calculateTotalDayLevelData(processedSubjects) {
  if (!processedSubjects || !Array.isArray(processedSubjects)) return 0
  return processedSubjects.reduce((total, subject) => 
    total + (subject.day_level_data?.length || 0), 0
  )
}

/**
 * Checks if cohort has curve features data
 * @param {Array} processedSubjects - Array of processed subject data
 * @returns {boolean} - True if any subject has curve features
 */
export function cohortHasCurveFeatures(processedSubjects) {
  if (!processedSubjects || !Array.isArray(processedSubjects)) return false
  return processedSubjects.some(subject => 
    subject.curve_features && subject.curve_features.length > 0
  )
}

/**
 * Checks if cohort has day-level data
 * @param {Array} processedSubjects - Array of processed subject data
 * @returns {boolean} - True if any subject has day-level data
 */
export function cohortHasDayLevelData(processedSubjects) {
  if (!processedSubjects || !Array.isArray(processedSubjects)) return false
  return processedSubjects.some(subject => 
    subject.day_level_data && subject.day_level_data.length > 0
  )
}

/**
 * Compiles cohort data for download by aggregating curve features and day-level data across subjects
 * @param {Object} cohortData - Raw cohort data object
 * @param {Object} downloadOptions - Selected download options
 * @param {Object} columnMetadata - Column ordering metadata from all subjects
 * @returns {Object} - Compiled data in the format expected by the backend
 */
export function compileCohortDataForDownload(cohortData, downloadOptions, columnMetadata = {}) {
  const compiledData = {
    // Note: We don't include configured_data (processed dataset) for cohorts
    // as it would be too much data to handle in memory
    curve_features: [],
    day_level_data: [],
    processing_summary: {},
    // Column metadata to preserve ordering
    column_metadata: {
      curve_features_order: null,
      day_level_data_order: null
    }
  }

  // Determine the most complete column order across all subjects
  const allCurveColumns = new Set()
  const allDayColumns = new Set()
  
  if (cohortData.processed_subjects) {
    cohortData.processed_subjects.forEach(subject => {
      if (subject.curve_features) {
        subject.curve_features.forEach(curve => {
          Object.keys(curve).forEach(col => allCurveColumns.add(col))
        })
      }
      if (subject.day_level_data) {
        subject.day_level_data.forEach(day => {
          Object.keys(day).forEach(col => allDayColumns.add(col))
        })
      }
    })
  }

  // Use the most common column order as template, but ensure all columns are included
  const curveTemplateOrder = columnMetadata.curve_features_order || []
  const dayTemplateOrder = columnMetadata.day_level_data_order || []
  
  const finalCurveOrder = [...curveTemplateOrder, ...Array.from(allCurveColumns).filter(col => !curveTemplateOrder.includes(col))]
  const finalDayOrder = [...dayTemplateOrder, ...Array.from(allDayColumns).filter(col => !dayTemplateOrder.includes(col))]
  
  compiledData.column_metadata.curve_features_order = finalCurveOrder
  compiledData.column_metadata.day_level_data_order = finalDayOrder

  // Compile curve features across all subjects with consistent column ordering
  if (downloadOptions.curve_features && cohortData.processed_subjects) {
    cohortData.processed_subjects.forEach(subject => {
      if (subject.curve_features && Array.isArray(subject.curve_features)) {
        const curvesWithSubid = subject.curve_features.map(curve => {
          const orderedCurve = {}
          // Add columns in the determined order
          finalCurveOrder.forEach(col => {
            if (curve.hasOwnProperty(col)) {
              orderedCurve[col] = curve[col]
            }
          })
          // Add any remaining columns not in the order
          Object.keys(curve).forEach(col => {
            if (!finalCurveOrder.includes(col)) {
              orderedCurve[col] = curve[col]
            }
          })
          return {
            ...orderedCurve,
            subid: subject.subid,
            dataset_id: subject.dataset_id || '001'
          }
        })
        compiledData.curve_features.push(...curvesWithSubid)
      }
    })
    console.log(`📊 Compiled ${compiledData.curve_features.length} curve features from ${cohortData.processed_subjects.length} subjects with column order:`, finalCurveOrder)
  }

  // Compile day-level data across all subjects with consistent column ordering
  if (downloadOptions.day_level_data && cohortData.processed_subjects) {
    cohortData.processed_subjects.forEach(subject => {
      if (subject.day_level_data && Array.isArray(subject.day_level_data)) {
        const daysWithSubid = subject.day_level_data.map(day => {
          const orderedDay = {}
          // Add columns in the determined order
          finalDayOrder.forEach(col => {
            if (day.hasOwnProperty(col)) {
              orderedDay[col] = day[col]
            }
          })
          // Add any remaining columns not in the order
          Object.keys(day).forEach(col => {
            if (!finalDayOrder.includes(col)) {
              orderedDay[col] = day[col]
            }
          })
          return {
            ...orderedDay,
            subid: subject.subid,
            dataset_id: subject.dataset_id || '001'
          }
        })
        compiledData.day_level_data.push(...daysWithSubid)
      }
    })
    console.log(`📊 Compiled ${compiledData.day_level_data.length} day-level entries from ${cohortData.processed_subjects.length} subjects with column order:`, finalDayOrder)
  }

  // Compile processing summary
  if (downloadOptions.processing_summary) {
    compiledData.processing_summary = {
      cohort_summary: cohortData.summary_stats || {},
      processing_settings: cohortData.processing_settings || {},
      total_subjects: cohortData.processed_subjects?.length || 0,
      total_curve_features: compiledData.curve_features.length,
      total_day_level_entries: compiledData.day_level_data.length,
      compilation_timestamp: new Date().toISOString()
    }
  }

  return compiledData
}
